import { useLocation } from "@remix-run/react";
import { IconChevronDown, IconSearch } from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { ConfirmDialog } from "#app/components/new-ui/confirm-dialog";
import { Button } from "#app/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "#app/components/ui/dialog";
import { RailTooltip } from "#app/components/ui/tooltip";
import { useOrgTeam } from "#app/contexts/org-team-context.tsx";
import { useProjectThreadsStore } from "#app/store/project-threads";
import {
  deleteProjectThread,
  listProjectThreads,
  type ProjectThread,
} from "#app/utils/projects/threads";
import { resolveSidebarProjectId } from "#app/utils/sidebar-recent";

import { ChatListItem } from "./chat-list-item";

const FETCH_LIMIT = 100;

const OPEN_THREAD_PATH = /^\/u\/projects\/[^/]+\/chat\/([^/]+)/;

type RecentProjectChatsProps = {
  /** Routes through the sidebar's design-phase navigation guard. */
  onNavigate: (to: string) => void;
  collapsed: boolean;
};

/** Project chat threads in the sidebar Recent slot. */
export function RecentProjectChats({
  onNavigate,
  collapsed,
}: RecentProjectChatsProps) {
  const { t } = useTranslation(["nav", "projects", "common"]);
  const location = useLocation();
  const { currentOrg, currentTeam } = useOrgTeam();

  const [searchOpen, setSearchOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ProjectThread | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const threads = useProjectThreadsStore((state) => state.threads);
  const total = useProjectThreadsStore((state) => state.total);
  const loading = useProjectThreadsStore((state) => state.loading);
  const listError = useProjectThreadsStore((state) => state.listError);
  const reloadToken = useProjectThreadsStore((state) => state.reloadToken);
  const beginLoad = useProjectThreadsStore((state) => state.beginLoad);
  const setThreads = useProjectThreadsStore((state) => state.setThreads);
  const setLoading = useProjectThreadsStore((state) => state.setLoading);
  const setListError = useProjectThreadsStore((state) => state.setListError);

  const projectId = resolveSidebarProjectId(location.pathname);
  const openThreadId = OPEN_THREAD_PATH.exec(location.pathname)?.[1] ?? null;

  const ctx = useMemo(
    () => ({
      orgName: currentOrg?.name ?? undefined,
      teamName: currentTeam?.name ?? undefined,
      teamKey: currentTeam?.prefix ?? undefined,
    }),
    [currentOrg?.name, currentTeam?.name, currentTeam?.prefix],
  );

  /** Only the newest load may write; overlapping fetches resolve out of order. */
  const requestIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!projectId) return;
    const requestId = ++requestIdRef.current;
    beginLoad(projectId);
    try {
      const result = await listProjectThreads(projectId, {
        limit: FETCH_LIMIT,
        ...ctx,
      });
      if (requestId !== requestIdRef.current) return;
      setThreads(
        result.threads,
        result.pagination?.total ?? result.threads.length,
      );
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setThreads([], 0);
      setListError(
        error instanceof Error ? error.message : t("nav:chatsLoadFailed"),
      );
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [projectId, ctx, beginLoad, setThreads, setLoading, setListError, t]);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  // Keyed on the project alone, so creating a chat does not clear the filter.
  useEffect(() => {
    setSearch("");
  }, [projectId]);

  const searchLabel = t("nav:searchConversationsTitle");

  const openSearchDialog = useCallback(() => {
    setSearch("");
    setSearchOpen(true);
  }, []);

  const handleSearchOpenChange = useCallback((open: boolean) => {
    setSearchOpen(open);
    if (!open) setSearch("");
  }, []);

  const selectThread = (thread: ProjectThread) => {
    setSearchOpen(false);
    setSearch("");
    onNavigate(`/u/projects/${projectId}/chat/${thread.id}`);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !projectId) return;
    setDeletingId(deleteTarget.id);
    try {
      await deleteProjectThread(projectId, deleteTarget.id, ctx);
      // The deleted thread's URL would render a dead pane.
      if (deleteTarget.id === openThreadId) {
        onNavigate(`/u/projects/${projectId}/chat`);
      }
      setDeleteTarget(null);
      await load();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("projects:chat.deleteFailed", "Failed to delete chat"),
      );
    } finally {
      setDeletingId(null);
    }
  };

  const untitled = t("projects:chat.untitled", "New chat");

  // `.trim()`: a whitespace-only title is not a title -- it renders a row with
  // no visible label at all.
  const labelFor = (thread: ProjectThread) => thread.title.trim() || untitled;

  const visibleThreads = search.trim()
    ? threads.filter((thread) =>
        labelFor(thread).toLowerCase().includes(search.trim().toLowerCase()),
      )
    : threads;

  const renderList = () => {
    if (visibleThreads.length > 0) {
      return visibleThreads.map((thread) => (
        <ChatListItem
          key={thread.id}
          title={labelFor(thread)}
          isActive={thread.id === openThreadId}
          isDeleting={thread.id === deletingId}
          onSelect={() => selectThread(thread)}
          onDelete={() => setDeleteTarget(thread)}
          deleteLabel={t("common:delete")}
          moreLabel={t("common:ui.moreActions")}
        />
      ));
    }

    if (loading) {
      return (
        <p className="px-3 py-2 text-sm text-muted-foreground">
          {t("common:loading")}
        </p>
      );
    }

    if (listError) {
      return (
        <div className="space-y-2 px-3 py-2">
          <p className="text-sm text-muted-foreground">{listError}</p>
          <Button variant="secondary" size="xs" onClick={() => void load()}>
            {t("nav:retryLoadChats")}
          </Button>
        </div>
      );
    }

    return (
      <p className="px-3 py-2 text-sm text-muted-foreground">
        {/* A filter that matches nothing is not an empty project. */}
        {search.trim() ? t("common:ui.noResults") : t("nav:noChats")}
      </p>
    );
  };

  const deleteDialog = (
    <ConfirmDialog
      open={Boolean(deleteTarget)}
      onOpenChange={(open) => {
        if (!open) setDeleteTarget(null);
      }}
      destructive
      loading={Boolean(deletingId)}
      title={t("projects:chat.deleteTitle", "Delete chat")}
      description={t(
        "projects:chat.deleteConfirm",
        "This removes the conversation. Runs it dispatched stay in the project's run log.",
      )}
      confirmText={t("common:delete")}
      cancelText={t("common:cancel")}
      onConfirm={() => void confirmDelete()}
    />
  );

  const searchDialog = (
    <Dialog open={searchOpen} onOpenChange={handleSearchOpenChange}>
      <DialogContent
        showCloseButton
        className="gap-0 overflow-hidden p-0 sm:max-w-2xl"
      >
        <div className="border-b px-5 py-3">
          <DialogTitle className="mb-2 text-sm font-semibold text-foreground">
            {searchLabel}
          </DialogTitle>
          <div className="flex h-10 items-center gap-3">
            <IconSearch className="h-5 w-5 text-zinc-500" />
            <input
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("nav:searchChats")}
              aria-label={searchLabel}
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-400"
            />
          </div>
        </div>
        <div className="h-115 overflow-y-auto overscroll-contain px-3 py-4 pr-5">
          <div className="space-y-1">{renderList()}</div>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (collapsed) {
    return (
      <div className="flex justify-center">
        <RailTooltip title={searchLabel}>
          <Button
            variant="ghost"
            size="icon"
            onClick={openSearchDialog}
            aria-label={searchLabel}
            className="-mx-2"
          >
            <IconSearch className="h-5 w-5" />
          </Button>
        </RailTooltip>
        {searchDialog}
        {deleteDialog}
      </div>
    );
  }

  return (
    <div className="recent-block mt-5 -mx-2 -mr-3">
      <div className="flex items-center justify-between pr-5">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className="ml-3 flex cursor-pointer items-center gap-1 text-[13px] text-zinc-500/80 hover:text-black dark:hover:text-zinc-100"
        >
          {t("nav:projectConversations")}
          <motion.span
            animate={{ rotate: expanded ? 0 : -90 }}
            transition={{ duration: 0.2 }}
            className="inline-flex"
          >
            <IconChevronDown size={18} />
          </motion.span>
        </button>

        <Button
          variant="secondary"
          size="xs"
          onClick={openSearchDialog}
          aria-label={searchLabel}
        >
          <IconSearch />
          {t("common:search")}
        </Button>
      </div>

      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="mt-3 h-[calc(100dvh-340px)] space-y-1 overflow-y-auto overscroll-contain pr-3">
              {renderList()}
              {!search.trim() && threads.length < total ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  {t("nav:showingOfTotal", {
                    count: threads.length,
                    total,
                    defaultValue: "Showing {{count}} of {{total}}",
                  })}
                </p>
              ) : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {searchDialog}
      {deleteDialog}
    </div>
  );
}

export default RecentProjectChats;
